import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from 'environments/environment';
import moment from 'moment';
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import SockJS from 'sockjs-client';
import { Client, Frame, over, Subscription } from 'stompjs';
import { AuthenticationService } from '../../core/auth/authentication.service';
import { Channel } from './model/channel';
import { ChannelsData } from './model/channels-data';
import { Message } from './model/message';
import { MessagesData } from './model/messages-data';
@Injectable({
  providedIn: 'root',
})
export class ChatService {
  currentChannel: Channel;

  channelsData = new ChannelsData();

  messagesData = new MessagesData();

  channelChanged$: Subject<Channel> = new Subject<Channel>();

  private _conected = false;

  private _stompClient: Client;

  private _currentChannelSub: Subscription;

  private _notificationSub: Subscription;

  private _chatNotificationAudio: HTMLAudioElement;

  constructor(private _http: HttpClient, private _authService: AuthenticationService, @Inject(PLATFORM_ID) private _platformId: Object) {
    if (isPlatformBrowser(this._platformId)) {
      this._chatNotificationAudio = new Audio('assets/chat-notification.mp3');
    }
  }

  get conected(): boolean {
    return this._conected;
  }

  initializeWebSocketConnection() {
    if (!this._authService.jwt || this.conected) {
      return;
    }
    this._stompClient = over(new SockJS(environment.backend.url + '/ws'));
    this._stompClient.debug = (i) => {};
    this._stompClient.connect(
      { Authorization: 'Bearer ' + this._authService.jwt.token },
      (connected: Frame) => {
        this._conected = true;
        this.connectToChannel(this.currentChannel);
        this.connectToNotification();
      },
      (error: any) => {
        console.log(error);
        this._conected = false;
        if (this._currentChannelSub) {
          this._currentChannelSub.unsubscribe();
        }
        if (this._notificationSub) {
          this._notificationSub.unsubscribe();
        }
      }
    );
  }

  disconnectWebSocketConnection() {
    this._conected = false;
    this.channelsData = new ChannelsData();
    this.resetCurrentChat();
    if (this._notificationSub) {
      this._notificationSub.unsubscribe();
    }
    if (this._stompClient != null) {
      this._stompClient.disconnect(() => {
        console.log('disconnected');
      });
    }
  }

  connectToChannel(channel: Channel) {
    this.resetCurrentChat();
    this.currentChannel = channel;
    if (this._conected && this.currentChannel) {
      this.currentChannel.unsentMessages.forEach((element) => this.messagesData.messages.push(element));
      this.channelChanged$.next(this.currentChannel);
      this.connectToCurrentChannel();
    }
  }

  sendMessage(message: string, filePath?: string) {
    if (!this._conected) {
      this.createUnsentMessages(message, filePath);
    } else {
      const newMessage: any = {
        sender: this._authService.currentUser.email,
        type: 'TEXT',
      };
      if (message) {
        newMessage.content = message;
      }
      if (filePath) {
        newMessage.media = filePath;
      }
      this._stompClient.send(`/chat/app/private.chat.${this.currentChannel.channelId}`, {}, JSON.stringify(newMessage));
    }
  }

  resend(message: Message) {
    if (this._conected) {
      const msg = this.messagesData.messages.splice(this.messagesData.messages.indexOf(message), 1)[0].content;
      const filePath = this.messagesData.messages.splice(this.messagesData.messages.indexOf(message), 1)[0].media;
      this.currentChannel.unsentMessages.splice(this.currentChannel.unsentMessages.indexOf(message), 1);
      this.sendMessage(msg, filePath);
    }
  }

  // TODO markMessageAsRead
  getMessagesForCurrentChannel(): Observable<Message[]> {
    if (!this.messagesData.getNextPage) {
      return of(this.messagesData.messages);
    }
    return this._http
      .get<any[]>(`api/private/chat/messages/${this.currentChannel.channelId}`, {
        params: {
          page: this.messagesData.pageNumber.toString(),
          size: this.messagesData.numberOfItems.toString(),
          sort: ['date,desc'],
        },
      })
      .pipe(
        map((response: any) => {
          if (response && response.messages && response.messages.content) {
            response.messages.content.forEach((message: any) => {
              const tempMessage = new Message(message);
              this.messagesData.messages.unshift(tempMessage);
              // this.markMessageAsRead(tempMessage);
            });
            this.messagesData.pageNumber++;
            if (response.messages.content.length < this.messagesData.numberOfItems) {
              this.messagesData.getNextPage = false;
            }
          } else {
            this.messagesData.getNextPage = false;
          }
          return this.messagesData.messages;
        })
      );
  }

  // TODO order channel
  getChannels(): Observable<Channel[]> {
    if (!this.channelsData.getNextPage) {
      return of(this.channelsData.channels);
    }
    return this._http
      .get<any[]>('api/private/chat/channels', {
        params: {
          page: this.channelsData.pageNumber.toString(),
          size: this.channelsData.numberOfItems.toString(),
          sort: 'lastMessage.date(desc)',
        },
      })
      .pipe(
        map((response: any) => {
          if (response && response.channels) {
            response.channels.forEach((channel: any) => {
              if (!this.findChannelById(channel.channelId)) {
                // if channel is already added(notification topic) prevent adding it again
                this.channelsData.channels.push(new Channel(channel));
              }
            });
            this.channelsData.pageNumber++;
            if (response.channels.length < this.channelsData.numberOfItems) {
              this.channelsData.getNextPage = false;
            }
          } else {
            this.channelsData.getNextPage = false;
          }
          return this.channelsData.channels;
        })
      );
  }

  downloadFile(url: string): Observable<any> {
    return this._http.get(url, { responseType: 'blob' });
  }

  uploadFile(file: File): Observable<any> {
    const endpoint = `api/private/storage/attachment/${this.currentChannel.channelId}`;
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this._http.post(endpoint, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }

  closeChannel(): Observable<any> {
    return this._http.patch(`api/private/chat/channel/${this.currentChannel.channelId}/close`, {}).pipe(
      map((result: any) => {
        if (result && result.message) {
          const temp = result.message.split(' ').pop();
          if (temp) {
            const utcDate = moment.utc(temp);
            this.currentChannel.closedDate = moment(utcDate).local().format('MM/DD/YY h:mm A');
          }
        }
        this.currentChannel.closed = true;
      })
    );
  }

  // TODO markMessageAsRead private
  markMessageAsRead(message: Message) {
    if (!message.read && message.sender !== this._authService.currentUser.email) {
      this._stompClient.send(`/private.chat.markRead.${message.messageId}`);
    }
  }

  private resetCurrentChat() {
    this.messagesData = new MessagesData();
    this.currentChannel = null;
    if (this._currentChannelSub) {
      this._currentChannelSub.unsubscribe();
    }
  }

  private connectToNotification() {
    const url = `/topic/private.chat.notification.${this._authService.currentUser.email}`;
    this._notificationSub = this._stompClient.subscribe(url, (event: any) => {
      if (event && event.body && event.command === 'MESSAGE') {
        const data = JSON.parse(event.body);
        const channel = this.findChannelById(data.channelId);
        if (channel) {
          channel.closed = false;
          channel.lastMessage = new Message({ content: data.shortContent, date: data.date });
          channel.numberOfUnread++; // TODO remove
          this.channelsData.channels.sort((a, b) => b.lastMessage.date.getTime() - a.lastMessage.date.getTime());
          this._chatNotificationAudio.play();
        } else {
          this._http.get<any[]>(`/api/private/chat/channel/${data.channelId}`).subscribe((result: any) => {
            const newChannel = new Channel(result.channel);
            newChannel.numberOfUnread++; // TODO remove
            this.channelsData.channels.unshift(newChannel);
            this._chatNotificationAudio.play();
          });
        }
      }
    });
  }

  private findChannelById(channelId: string): Channel {
    for (const i in this.channelsData.channels) {
      if (this.channelsData.channels[i].channelId === channelId) {
        return this.channelsData.channels[i];
      }
    }
    return null;
  }

  // TODO markMessageAsRead
  private connectToCurrentChannel() {
    const url = `/topic/private.chat.${this.currentChannel.channelId}`;
    this._currentChannelSub = this._stompClient.subscribe(url, (event: any) => {
      if (event && event.body) {
        const data = JSON.parse(event.body);
        if (data.type === 'TEXT') {
          const message = new Message(data);
          this.messagesData.messages.push(message);
          // this.markMessageAsRead(message);
        }
      }
    });
    this._stompClient.send(`/chat/app/chat.joinUser.${this.currentChannel.channelId}`, {}, JSON.stringify({ type: 'JOIN' }));
  }

  private createUnsentMessages(message: string, filePath?: string) {
    const newMessage = new Message({
      content: message,
      date: moment().toDate(),
      sender: this._authService.currentUser.email,
      type: 'TEXT',
      notSent: true,
      media: filePath,
    });
    this.messagesData.messages.push(newMessage);
    this.currentChannel.unsentMessages.push(newMessage);
  }
}
